/**
 * Trip Statement — native (vector) PDF renderer.
 *
 * Draws the statement directly with jsPDF text/shape primitives instead of
 * rasterizing HTML through html2canvas. That difference is the whole point:
 *
 *   - Text is real vector text — crisp at every zoom level, selectable,
 *     searchable, and the file weighs ~30KB instead of a ~2MB bitmap.
 *   - Pagination is controlled — sections and table rows break at row
 *     boundaries (jspdf-autotable handles the payment table), never mid-row.
 *   - Font sizes are proper print sizes (8pt body) rather than whatever
 *     a shrunken bitmap happens to scale to.
 *
 * Reads ONLY from the TripDocumentData object produced by
 * buildTripDocumentData(); layout mirrors the HTML renderer
 * (tripDocumentHtml.js), which remains as the desktop print-dialog fallback.
 *
 * jspdf + jspdf-autotable are imported dynamically by the caller-facing
 * entry point so they never contribute to the initial bundle.
 */

import {
  formatLongDate,
  formatShortDate,
  formatTime12,
  formatMoney,
  formatMoneyCents,
} from '@/utils/tripDocument';

/* Letter geometry, in points. */
const PAGE_W = 612;
const PAGE_H = 792;
const M = 44;              // left/right margin
const TOP = 40;
const BOTTOM = 46;
const CONTENT_W = PAGE_W - M * 2;

/* Palette — mirrors the CSS variables in tripDocumentHtml.js. */
const COL = {
  brand: [15, 25, 82],
  text: [16, 20, 38],
  slate: [51, 64, 93],
  muted: [91, 100, 120],
  faint: [138, 146, 166],
  rule: [219, 224, 235],
  ruleLight: [232, 236, 244],
  soft: [244, 246, 251],
  softBorder: [223, 228, 238],
  cardBg: [251, 252, 254],
  green: [17, 122, 68],
  greenText: [19, 88, 56],
  greenBg: [238, 250, 241],
  greenBorder: [202, 232, 209],
  red: [178, 59, 59],
  redBg: [253, 236, 236],
  redBorder: [241, 205, 205],
  brandBg: [233, 237, 250],
  brandBorder: [208, 216, 240],
  upcoming: [151, 161, 184],
};

function setFill(doc, c) { doc.setFillColor(c[0], c[1], c[2]); }
function setDraw(doc, c) { doc.setDrawColor(c[0], c[1], c[2]); }
function setText(doc, c) { doc.setTextColor(c[0], c[1], c[2]); }

/** Set font + size + color in one call; returns doc for chaining. */
function font(doc, size, style = 'normal', color = COL.text, family = 'helvetica') {
  doc.setFont(family, style);
  doc.setFontSize(size);
  setText(doc, color);
  return doc;
}

function textWidth(doc, str) {
  return doc.getTextWidth(String(str));
}

/**
 * Width of an uppercase letter-spaced run. jsPDF's getTextWidth ignores
 * charSpace, so the tracking has to be added manually — without this the
 * chip values render on top of their labels.
 */
function upperTextWidth(doc, str, size, charSpace) {
  font(doc, size, 'bold');
  const s = String(str).toUpperCase();
  return doc.getTextWidth(s) + charSpace * s.length;
}

/**
 * The standard-14 Helvetica font has no U+2192 (→) glyph — jsPDF renders
 * it as garbage. Draw the arrow as vector strokes instead.
 * `midY` is the vertical center of the arrow. Returns the arrow width.
 */
function drawArrow(doc, x, midY, len = 9, color = COL.faint) {
  setDraw(doc, color);
  doc.setLineWidth(0.9);
  doc.setLineCap('round');
  doc.setLineJoin('round');
  doc.line(x, midY, x + len, midY);
  doc.line(x + len - 3, midY - 2.4, x + len, midY);
  doc.line(x + len - 3, midY + 2.4, x + len, midY);
  return len;
}

/** Split "A → B" on the arrow; returns [left, right] or null. */
function splitOnArrow(str) {
  const parts = String(str || '').split('→').map((p) => p.trim());
  return parts.length === 2 ? parts : null;
}

function hRule(doc, y, color = COL.rule, width = 0.6, x1 = M, x2 = PAGE_W - M) {
  setDraw(doc, color);
  doc.setLineWidth(width);
  doc.line(x1, y, x2, y);
}

/**
 * Small uppercase label with letter-spacing (via charSpace). Resets
 * charSpace afterwards so it never leaks into subsequent text runs.
 */
function upperLabel(doc, str, x, y, size, color = COL.muted, opts = {}) {
  font(doc, size, 'bold', color);
  doc.setCharSpace(0.45);
  doc.text(String(str).toUpperCase(), x, y, { baseline: 'top', ...opts });
  doc.setCharSpace(0);
}

/** Compute the box width of a chip without drawing it. */
function chipWidth(doc, label, value) {
  const labelW = upperTextWidth(doc, label, 5, 0.4);
  font(doc, 8, 'bold');
  const valueW = textWidth(doc, value);
  return 7 + labelW + 6 + valueW + 7;
}

/** Draw label+value chip (booking reference / airline confirmation). */
function drawChip(doc, { label, value, x, y, tone = 'neutral' }) {
  const palette = tone === 'green'
    ? { bg: COL.greenBg, border: COL.greenBorder, label: COL.greenText, value: COL.greenText }
    : { bg: COL.soft, border: COL.rule, label: COL.muted, value: COL.text };

  const labelW = upperTextWidth(doc, label, 5, 0.4);
  font(doc, 8, 'bold');
  const valueW = textWidth(doc, value);

  const padX = 7;
  const gap = 6;
  const w = padX + labelW + gap + valueW + padX;
  const h = 15;

  setFill(doc, palette.bg);
  setDraw(doc, palette.border);
  doc.setLineWidth(0.6);
  doc.roundedRect(x, y, w, h, 3, 3, 'FD');

  upperLabel(doc, label, x + padX, y + 4.6, 5, palette.label);
  font(doc, 8, 'bold', palette.value);
  doc.text(value, x + padX + labelW + gap, y + 3.6, { baseline: 'top' });

  return { w, h };
}

function statusTone(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'completed' || s === 'confirmed') {
    return { bg: COL.greenBg, border: COL.greenBorder, text: COL.greenText };
  }
  if (s === 'defaulted' || s === 'cancelled' || s === 'canceled') {
    return { bg: COL.redBg, border: COL.redBorder, text: COL.red };
  }
  return { bg: COL.brandBg, border: COL.brandBorder, text: COL.brand };
}

/** Slim "continued" header drawn on pages 2+. Returns content start Y. */
function drawContinuationHeader(doc, reference) {
  upperLabel(doc, `Trip Statement · ${reference} · continued`, M, 22, 6, COL.faint);
  hRule(doc, 34, COL.ruleLight, 0.5);
  return 44;
}

/** Page-break-aware cursor. `reference` is the customer-facing booking reference. */
function makeCtx(doc, reference) {
  return {
    doc,
    y: 0,
    reference,
    ensure(needed) {
      if (this.y + needed > PAGE_H - BOTTOM) {
        doc.addPage();
        this.y = drawContinuationHeader(doc, this.reference);
      }
    },
  };
}

/* ── Header ──────────────────────────────────────────────────────────── */

function drawHeader(doc, data, logoDataUri) {
  const brand = data.document.brand;

  let logoBottom = TOP;
  if (logoDataUri) {
    try {
      const props = doc.getImageProperties(logoDataUri);
      const h = 20;
      const w = (props.width / props.height) * h;
      doc.addImage(logoDataUri, 'PNG', M, TOP, w, h);
      logoBottom = TOP + h;
    } catch {
      logoDataUri = null;
    }
  }
  if (!logoDataUri) {
    font(doc, 17, 'bold', COL.brand);
    doc.setCharSpace(1.4);
    doc.text('DEFERA', M, TOP, { baseline: 'top' });
    doc.setCharSpace(0);
    logoBottom = TOP + 17;
  }
  font(doc, 7.5, 'normal', COL.muted);
  doc.text(brand.tagline, M, logoBottom + 5, { baseline: 'top' });

  const rx = PAGE_W - M;
  font(doc, 15, 'bold', COL.brand);
  doc.setCharSpace(0.3);
  doc.text(data.document.title.toUpperCase(), rx, TOP - 2, { align: 'right', baseline: 'top' });
  doc.setCharSpace(0);
  font(doc, 6.5, 'normal', COL.muted);
  doc.text('Booking summary & payment schedule', rx, TOP + 15, { align: 'right', baseline: 'top' });
  font(doc, 8, 'bold', COL.slate, 'courier');
  doc.text(data.document.number, rx, TOP + 24, { align: 'right', baseline: 'top' });
  font(doc, 6.5, 'normal', COL.muted);
  doc.text(
    `Issued ${formatLongDate(data.document.issueDate.toISOString())}`,
    rx, TOP + 34, { align: 'right', baseline: 'top' },
  );

  const ruleY = TOP + 44;
  setDraw(doc, COL.brand);
  doc.setLineWidth(1.6);
  doc.line(M, ruleY, PAGE_W - M, ruleY);
  return ruleY + 11;
}

/* ── Banner: route · chips · status pill ─────────────────────────────── */

function drawBanner(ctx, data) {
  const { doc } = ctx;
  const { trip } = data;

  const tone = statusTone(trip.status);
  const statusLabel = trip.statusLabel;
  const statusW = upperTextWidth(doc, statusLabel, 7, 0.45) + 16;
  const statusX = PAGE_W - M - statusW;

  // Route — "City → City" with the arrow drawn as vector strokes (the
  // built-in Helvetica has no U+2192 glyph).
  const route = trip.route || trip.routeShort || 'Trip';
  const routeParts = splitOnArrow(route);
  font(doc, 13, 'bold', COL.text);
  let routeEndX = M;
  if (routeParts) {
    doc.text(routeParts[0], routeEndX, ctx.y + 1.5, { baseline: 'top' });
    routeEndX += textWidth(doc, routeParts[0]) + 6;
    routeEndX += drawArrow(doc, routeEndX, ctx.y + 8, 11, COL.faint) + 6;
    font(doc, 13, 'bold', COL.text);
    doc.text(routeParts[1], routeEndX, ctx.y + 1.5, { baseline: 'top' });
    routeEndX += textWidth(doc, routeParts[1]);
  } else {
    doc.text(route, routeEndX, ctx.y + 1.5, { baseline: 'top' });
    routeEndX += textWidth(doc, route);
  }

  // Chips flow after the route; wrap below when there isn't enough room
  // before the status pill.
  const chips = [];
  if (trip.reference) chips.push({ label: 'Booking reference', value: trip.reference, tone: 'neutral' });
  if (trip.confirmationDisclosed && trip.airlineConfirmation) {
    chips.push({ label: 'Airline confirmation', value: trip.airlineConfirmation, tone: 'green' });
  }

  let chipX = routeEndX + 12;
  let chipY = ctx.y;
  let bannerBottom = ctx.y + 16;
  chips.forEach((chip) => {
    const w = chipWidth(doc, chip.label, chip.value);
    if (chipX + w > statusX - 10) {
      chipX = M;
      chipY = bannerBottom + 4;
    }
    const { w: drawnW, h } = drawChip(doc, { ...chip, x: chipX, y: chipY });
    chipX += drawnW + 6;
    bannerBottom = Math.max(bannerBottom, chipY + h);
  });

  setFill(doc, tone.bg);
  setDraw(doc, tone.border);
  doc.setLineWidth(0.6);
  doc.roundedRect(statusX, ctx.y, statusW, 15, 7.5, 7.5, 'FD');
  upperLabel(doc, statusLabel, statusX + 8, ctx.y + 4.6, 7, tone.text);

  ctx.y = bannerBottom + 7;
  hRule(doc, ctx.y, COL.rule, 0.6);
  ctx.y += 9;
}

/* ── Trip meta strip (4 columns) ─────────────────────────────────────── */

function drawMetaStrip(ctx, data) {
  const { doc } = ctx;
  const { trip } = data;
  const cells = [
    ['Trip type', `${trip.type}${trip.cabinClass ? ` · ${trip.cabinClass}` : ''}`],
    ['Departure', formatLongDate(trip.departureDate) || '—'],
    ['Passengers', String(trip.passengerCount || '—')],
    ['Booked on', formatLongDate(trip.bookingDate) || '—'],
  ];
  const colW = CONTENT_W / cells.length;
  cells.forEach(([label, value], i) => {
    const x = M + i * colW;
    upperLabel(doc, label, x, ctx.y, 5.5, COL.faint);
    font(doc, 8.5, 'bold', COL.text);
    doc.text(String(value), x, ctx.y + 9, { baseline: 'top', maxWidth: colW - 10 });
  });
  ctx.y += 22;
  hRule(doc, ctx.y, COL.rule, 0.6);
  ctx.y += 11;
}

/* ── Customer / Cost breakdown panels ────────────────────────────────── */

function costRows(pricing) {
  const rows = [['Base fare', formatMoney(pricing.baseFare, pricing.currency)]];
  // Mirrors renderCostBreakdown in tripDocumentHtml.js — the PDF and the HTML
  // statement must itemize identically.
  if (pricing.seatTotal > 0) {
    const seatLabel = pricing.seatCount > 1 ? `Seat selection (${pricing.seatCount})` : 'Seat selection';
    rows.push([seatLabel, formatMoney(pricing.seatTotal, pricing.currency)]);
  }
  if (pricing.creditsTotal > 0) {
    rows.push(['Credits applied', `- ${formatMoney(pricing.creditsTotal, pricing.currency)}`]);
  }
  if (pricing.serviceFee > 0) rows.push(['Service fee', formatMoney(pricing.serviceFee, pricing.currency)]);
  if (pricing.holdFee > 0) rows.push(['Hold fee', formatMoney(pricing.holdFee, pricing.currency)]);
  // Added after the fees: Faye's price, financed with the trip.
  if (pricing.insurance > 0) {
    rows.push([`Travel insurance${pricing.insurancePlan ? ` (${pricing.insurancePlan})` : ''}`, formatMoney(pricing.insurance, pricing.currency)]);
  }
  return rows;
}

function drawPanels(ctx, data) {
  const { doc } = ctx;
  const { customer, pricing } = data;
  const gap = 16;
  const colW = (CONTENT_W - gap) / 2;
  const leftX = M;
  const rightX = M + colW + gap;

  const kv = [
    ['Name', customer.name],
    ['Email', customer.email],
    ['Phone', customer.phone],
  ].filter(([, v]) => v);

  const cost = costRows(pricing);
  const rowH = 12;
  const padY = 8;
  const titleH = 12;
  // total row gets a rule + extra breathing room
  const rightH = padY + titleH + cost.length * rowH + 6 + rowH + padY;
  const leftH = padY + titleH + kv.length * rowH + padY;
  const panelH = Math.max(leftH, rightH);
  ctx.ensure(panelH + 14);
  const top = ctx.y;

  // Left panel — hairline border only.
  setDraw(doc, COL.softBorder);
  doc.setLineWidth(0.6);
  doc.roundedRect(leftX, top, colW, panelH, 4, 4, 'S');
  upperLabel(doc, 'Customer', leftX + 10, top + padY, 6.5, COL.brand);
  let y = top + padY + titleH;
  kv.forEach(([label, value]) => {
    font(doc, 8, 'normal', COL.muted);
    doc.text(label, leftX + 10, y, { baseline: 'top' });
    font(doc, 8, 'bold', COL.text);
    doc.text(String(value), leftX + colW - 10, y, { align: 'right', baseline: 'top' });
    y += rowH;
  });

  // Right panel — soft fill.
  setFill(doc, COL.soft);
  setDraw(doc, COL.softBorder);
  doc.roundedRect(rightX, top, colW, panelH, 4, 4, 'FD');
  upperLabel(doc, 'Cost breakdown', rightX + 10, top + padY, 6.5, COL.brand);
  y = top + padY + titleH;
  cost.forEach(([label, value]) => {
    font(doc, 8, 'normal', COL.slate);
    doc.text(label, rightX + 10, y, { baseline: 'top' });
    font(doc, 8, 'bold', COL.text);
    doc.text(value, rightX + colW - 10, y, { align: 'right', baseline: 'top' });
    y += rowH;
  });
  setDraw(doc, COL.rule);
  doc.setLineWidth(0.6);
  doc.line(rightX + 10, y + 1, rightX + colW - 10, y + 1);
  y += 6;
  font(doc, 8.5, 'bold', COL.brand);
  doc.text('Trip total', rightX + 10, y, { baseline: 'top' });
  font(doc, 9.5, 'bold', COL.brand);
  doc.text(
    formatMoney(pricing.grandTotalWithHold, pricing.currency),
    rightX + colW - 10, y - 1, { align: 'right', baseline: 'top' },
  );

  ctx.y = top + panelH + 14;
}

/* ── Section title ───────────────────────────────────────────────────── */

function drawSectionTitle(ctx, title) {
  const { doc } = ctx;
  ctx.ensure(30);
  upperLabel(doc, title, M, ctx.y, 7, COL.brand);
  hRule(doc, ctx.y + 11, COL.ruleLight, 0.5);
  ctx.y += 16;
}

/**
 * Embed an airline logo PNG into the PDF via jsPDF `addImage`. jsPDF only
 * accepts raster formats (PNG/JPEG) — SVG data URIs and svg2pdf vector
 * embedding both failed silently on customer devices, so we always
 * rasterize upstream in DownloadTripDocumentButton and pass a PNG here.
 */
function drawAirlineLogo(doc, pngDataUri, x, y, size) {
  if (!pngDataUri?.startsWith('data:image/png') && !pngDataUri?.startsWith('data:image/jpeg')) {
    return false;
  }
  try {
    doc.addImage(
      pngDataUri,
      pngDataUri.startsWith('data:image/png') ? 'PNG' : 'JPEG',
      x, y, size, size,
    );
    return true;
  } catch {
    return false;
  }
}

/* ── Flight itinerary ────────────────────────────────────────────────── */

function groupSegments(segments) {
  const groups = [];
  let currentSlice = -1;
  let current = null;
  segments.forEach((seg) => {
    if (seg.sliceIndex !== currentSlice) {
      current = { sliceIndex: seg.sliceIndex, legs: [] };
      groups.push(current);
      currentSlice = seg.sliceIndex;
    }
    current.legs.push(seg);
  });
  return groups;
}

async function drawItinerary(ctx, data) {
  const { doc } = ctx;
  const groups = groupSegments(data.segments);
  if (!groups.length) return;

  drawSectionTitle(ctx, 'Flight itinerary');

  for (let gIdx = 0; gIdx < groups.length; gIdx += 1) {
    const group = groups[gIdx];
    const firstLeg = group.legs[0];
    const lastLeg = group.legs[group.legs.length - 1];
    const groupLabel = groups.length > 1
      ? (gIdx === 0 ? 'Outbound' : gIdx === 1 ? 'Return' : `Leg ${gIdx + 1}`)
      : 'Itinerary';

    ctx.ensure(14 + 30); // slice head + at least one leg card
    font(doc, 8.5, 'bold', COL.text);
    let hx = M;
    doc.text(`${groupLabel} · ${firstLeg.origin}`, hx, ctx.y, { baseline: 'top' });
    hx += textWidth(doc, `${groupLabel} · ${firstLeg.origin}`) + 5;
    hx += drawArrow(doc, hx, ctx.y + 4.2, 9, COL.faint) + 5;
    font(doc, 8.5, 'bold', COL.text);
    doc.text(lastLeg.destination, hx, ctx.y, { baseline: 'top' });
    font(doc, 7.5, 'normal', COL.muted);
    doc.text(formatLongDate(firstLeg.departureDate) || '', PAGE_W - M, ctx.y + 1, { align: 'right', baseline: 'top' });
    ctx.y += 12;

    for (const leg of group.legs) {
      const flightNo = leg.marketingCarrierCode && leg.flightNumber
        ? `${leg.marketingCarrierCode}${leg.flightNumber}`
        : (leg.flightNumber || '');
      const metaBits = [leg.marketingCarrier, flightNo, leg.aircraft, leg.cabinClass]
        .filter(Boolean).join(' · ');

      const textStartX = M + 9 + 20;
      font(doc, 7, 'normal', COL.muted);
      const metaMaxW = PAGE_W - M - textStartX - 9;
      const metaLines = metaBits ? doc.splitTextToSize(metaBits, metaMaxW) : [];
      const cardH = 19 + (metaLines.length ? 3.5 + metaLines.length * 8.5 : 0);
      ctx.ensure(cardH + 5);

      setFill(doc, COL.cardBg);
      setDraw(doc, COL.softBorder);
      doc.setLineWidth(0.6);
      doc.roundedRect(M, ctx.y, CONTENT_W, cardH, 4, 4, 'FD');

      let x = M + 9;
      const rowY = ctx.y + 6;
      drawAirlineLogo(doc, leg.marketingCarrierLogoPng, x, rowY - 1.5, 14);
      x = textStartX;

      font(doc, 9.5, 'bold', COL.text);
      const depTime = formatTime12(leg.departingAt) || '—';
      doc.text(depTime, x, rowY, { baseline: 'top' });
      x += textWidth(doc, depTime) + 5;
      font(doc, 8.5, 'bold', COL.brand);
      doc.text(leg.origin || '—', x, rowY + 0.8, { baseline: 'top' });
      x += textWidth(doc, leg.origin || '—') + 8;
      x += drawArrow(doc, x, rowY + 4.5, 10, COL.faint) + 8;
      font(doc, 9.5, 'bold', COL.text);
      const arrTime = formatTime12(leg.arrivingAt) || '—';
      doc.text(arrTime, x, rowY, { baseline: 'top' });
      x += textWidth(doc, arrTime) + 5;
      font(doc, 8.5, 'bold', COL.brand);
      doc.text(leg.destination || '—', x, rowY + 0.8, { baseline: 'top' });

      if (metaLines.length) {
        font(doc, 7, 'normal', COL.muted);
        doc.text(metaLines, textStartX, ctx.y + 19, { baseline: 'top', lineHeightFactor: 1.2 });
      }
      ctx.y += cardH + 4;
    }
    ctx.y += 4;
  }
  ctx.y += 2;
}

/* ── Passengers ──────────────────────────────────────────────────────── */

function drawPassengers(ctx, data) {
  const { doc } = ctx;
  drawSectionTitle(ctx, 'Passengers');

  if (!data.passengers.length) {
    font(doc, 8, 'normal', COL.muted);
    doc.text('No passengers on file.', M, ctx.y, { baseline: 'top' });
    ctx.y += 16;
    return;
  }

  data.passengers.forEach((p, idx) => {
    const meta = [];
    if (p.dateOfBirth) meta.push(`DOB ${formatShortDate(p.dateOfBirth)}`);
    if (p.gender) meta.push(p.gender.charAt(0).toUpperCase() + p.gender.slice(1));
    if (p.type && p.type !== 'adult') meta.push(p.type.charAt(0).toUpperCase() + p.type.slice(1));
    if (p.nationality) meta.push(`Nat. ${p.nationality}`);

    const trailerBits = [];
    if (p.email) trailerBits.push(p.email);
    if (p.phoneNumber) trailerBits.push(p.phoneNumber);
    if (p.knownTravelerNumber) trailerBits.push(`KTN ${p.knownTravelerNumber}`);
    if (p.frequentFlyerProgram || p.frequentFlyerNumber) {
      trailerBits.push(`FF ${[p.frequentFlyerProgram, p.frequentFlyerNumber].filter(Boolean).join(' ')}`);
    }
    if (p.seatPreference) trailerBits.push(`Seat: ${p.seatPreference}`);
    const trailer = trailerBits.join(' · ');

    const rowH = trailer ? 24 : 16;
    ctx.ensure(rowH);

    // Number badge.
    setFill(doc, COL.brandBg);
    doc.circle(M + 7, ctx.y + 6, 7, 'F');
    font(doc, 7, 'bold', COL.brand);
    doc.text(String(idx + 1), M + 7, ctx.y + 3, { align: 'center', baseline: 'top' });

    let x = M + 20;
    font(doc, 8.5, 'bold', COL.text);
    doc.text(p.fullName || 'Passenger', x, ctx.y + 1, { baseline: 'top' });
    if (meta.length) {
      x += textWidth(doc, p.fullName || 'Passenger') + 4;
      font(doc, 7, 'normal', COL.muted);
      doc.text(`· ${meta.join(' · ')}`, x, ctx.y + 2.6, { baseline: 'top' });
    }
    if (trailer) {
      font(doc, 7, 'normal', COL.muted);
      doc.text(trailer, M + 20, ctx.y + 12, { baseline: 'top', maxWidth: CONTENT_W - 20 });
    }
    ctx.y += rowH;
    if (idx < data.passengers.length - 1) {
      hRule(doc, ctx.y - 4, COL.ruleLight, 0.4, M + 20);
    }
  });
  ctx.y += 4;
}

/* ── Payment plan & history ──────────────────────────────────────────── */

function drawPaymentStrip(ctx, data) {
  const { doc } = ctx;
  const { payment } = data;

  ctx.ensure(24);
  setFill(doc, COL.soft);
  setDraw(doc, COL.softBorder);
  doc.setLineWidth(0.6);
  doc.roundedRect(M, ctx.y, CONTENT_W, 18, 4, 4, 'FD');

  const parts = [{ label: 'Plan: ', value: payment.methodLabel }];
  if (payment.frequency) {
    parts.push({ label: 'Frequency: ', value: payment.frequency === 'biweekly' ? 'Bi-weekly' : 'Weekly' });
  }
  if (payment.depositPercentage != null) {
    parts.push({ label: 'Deposit: ', value: `${payment.depositPercentage}%` });
  }
  let x = M + 10;
  const ty = ctx.y + 5.4;
  parts.forEach((part, i) => {
    if (i > 0) {
      font(doc, 7.5, 'normal', COL.faint);
      doc.text('·', x, ty, { baseline: 'top' });
      x += textWidth(doc, '·') + 6;
    }
    font(doc, 7.5, 'normal', COL.muted);
    doc.text(part.label, x, ty, { baseline: 'top' });
    x += textWidth(doc, part.label);
    font(doc, 7.5, 'bold', COL.text);
    doc.text(part.value, x, ty, { baseline: 'top' });
    x += textWidth(doc, part.value) + 6;
  });

  const paidTxt = formatMoneyCents(payment.totalPaidCents, payment.currency);
  const totalTxt = formatMoneyCents(payment.totalCents, payment.currency);
  const rx = PAGE_W - M - 10;
  font(doc, 7.5, 'bold', COL.text);
  const totalW = textWidth(doc, totalTxt);
  const paidW = textWidth(doc, paidTxt);
  font(doc, 7.5, 'normal', COL.muted);
  const midW = textWidth(doc, ' of ');
  const suffixW = textWidth(doc, ' paid');
  let px = rx - (paidW + midW + totalW + suffixW);
  font(doc, 7.5, 'bold', COL.text);
  doc.text(paidTxt, px, ty, { baseline: 'top' });
  px += paidW;
  font(doc, 7.5, 'normal', COL.muted);
  doc.text(' of ', px, ty, { baseline: 'top' });
  px += midW;
  font(doc, 7.5, 'bold', COL.text);
  doc.text(totalTxt, px, ty, { baseline: 'top' });
  px += totalW;
  font(doc, 7.5, 'normal', COL.muted);
  doc.text(' paid', px, ty, { baseline: 'top' });

  ctx.y += 24;
}

function paymentRowTone(status) {
  if (status === 'paid' || status === 'paid_ahead') return { dot: COL.green, text: COL.greenText };
  if (status === 'past_due') return { dot: COL.red, text: COL.red };
  return { dot: COL.upcoming, text: COL.muted };
}

function drawPaymentTable(ctx, data, autoTable) {
  const { doc } = ctx;
  const { payment } = data;
  const rows = [
    ...payment.paidRows.map((r) => ({ ...r, hint: 'Paid' })),
    ...payment.pastDueRows.map((r) => ({ ...r, hint: 'Past due' })),
    ...payment.upcomingRows.map((r) => ({ ...r, hint: 'Upcoming' })),
  ];

  if (!rows.length) {
    font(doc, 8, 'normal', COL.muted);
    doc.text('No payment schedule on file.', M, ctx.y, { baseline: 'top' });
    ctx.y += 16;
    return;
  }

  const body = rows.map((r) => [
    r.hint,
    formatLongDate(r.date) || '—',
    r.label || '',
    r.amountCents === 0 ? '—' : formatMoneyCents(r.amountCents, r.currency || payment.currency),
  ]);

  const foot = [
    ['', '', 'Paid to date', formatMoneyCents(payment.totalPaidCents, payment.currency)],
    ['', '', 'Balance remaining', formatMoneyCents(payment.remainingCents, payment.currency)],
    ['', '', 'Trip total', formatMoneyCents(payment.totalCents, payment.currency)],
  ];

  autoTable(doc, {
    startY: ctx.y,
    margin: { left: M, right: M, top: 44, bottom: BOTTOM + 6 },
    head: [['Status', 'Date', 'Description', 'Amount']],
    body,
    foot,
    showFoot: 'lastPage',
    theme: 'plain',
    styles: {
      font: 'helvetica',
      fontSize: 7.5,
      textColor: COL.text,
      cellPadding: { top: 2.4, bottom: 2.4, left: 4, right: 4 },
    },
    headStyles: {
      fontSize: 5.75,
      fontStyle: 'bold',
      textColor: COL.faint,
      cellPadding: { top: 2, bottom: 3, left: 4, right: 4 },
    },
    footStyles: {
      fontSize: 8,
      fontStyle: 'bold',
      textColor: COL.text,
      cellPadding: { top: 2.4, bottom: 2.4, left: 4, right: 4 },
    },
    // Column 2 is given a fixed width (rather than 'auto', which sizes to
    // content and can leave the table narrower than the page — the exact
    // bug that made "Paid to date / Balance remaining / Trip total" and
    // every amount column sit well short of the right margin). Fixed
    // widths on every column force the table to span the full content
    // width, right-aligning amounts flush against the page margin.
    columnStyles: {
      0: { cellWidth: 66, cellPadding: { top: 2.4, bottom: 2.4, left: 12, right: 4 } },
      1: { cellWidth: 108 },
      2: { cellWidth: CONTENT_W - 66 - 108 - 84 },
      3: { cellWidth: 84, halign: 'right', fontStyle: 'bold' },
    },
    didParseCell(hook) {
      if (hook.section === 'head') {
        hook.cell.text = hook.cell.text.map((t) => t.toUpperCase());
        if (hook.column.index === 3) hook.cell.styles.halign = 'right';
        if (hook.column.index === 0) hook.cell.styles.cellPadding = { top: 2, bottom: 3, left: 12, right: 4 };
      }
      if (hook.section === 'body') {
        const tone = paymentRowTone(rows[hook.row.index]?.status);
        if (hook.column.index === 0) {
          hook.cell.styles.textColor = tone.text;
          hook.cell.styles.fontSize = 6.75;
          hook.cell.styles.fontStyle = 'bold';
        }
        if (hook.column.index === 1) hook.cell.styles.textColor = COL.slate;
        if (hook.column.index === 2) hook.cell.styles.textColor = COL.muted;
      }
      if (hook.section === 'foot') {
        if (hook.column.index === 2) {
          hook.cell.styles.halign = 'right';
          hook.cell.styles.textColor = COL.muted;
          hook.cell.styles.fontStyle = 'normal';
          hook.cell.styles.fontSize = 7.5;
        }
        if (hook.row.index === 2) {
          hook.cell.styles.textColor = COL.brand;
          hook.cell.styles.fontSize = 9;
          if (hook.column.index === 2) {
            hook.cell.styles.fontStyle = 'bold';
            hook.cell.styles.fontSize = 8;
          }
        }
      }
    },
    didDrawCell(hook) {
      if (hook.section === 'body' && hook.column.index === 0) {
        const tone = paymentRowTone(rows[hook.row.index]?.status);
        setFill(doc, tone.dot);
        doc.circle(hook.cell.x + 5.5, hook.cell.y + hook.cell.height / 2, 1.7, 'F');
      }
      // Row separators: light rule under each body row, stronger rule
      // above the totals block.
      if (hook.column.index === 3) {
        if (hook.section === 'body') {
          setDraw(doc, COL.ruleLight);
          doc.setLineWidth(0.4);
          doc.line(M, hook.cell.y + hook.cell.height, PAGE_W - M, hook.cell.y + hook.cell.height);
        }
        if (hook.section === 'foot' && hook.row.index === 0) {
          setDraw(doc, COL.rule);
          doc.setLineWidth(0.8);
          doc.line(M, hook.cell.y, PAGE_W - M, hook.cell.y);
        }
      }
      if (hook.section === 'head' && hook.column.index === 3) {
        setDraw(doc, COL.rule);
        doc.setLineWidth(0.6);
        doc.line(M, hook.cell.y + hook.cell.height, PAGE_W - M, hook.cell.y + hook.cell.height);
      }
    },
    didDrawPage() {
      // Slim header on every continuation page the table touches. When
      // ensure() already drew one (table started on a page it created),
      // this overprints the exact same glyphs at the same coordinates —
      // visually a no-op.
      if (doc.internal.getCurrentPageInfo().pageNumber > 1) {
        drawContinuationHeader(doc, ctx.reference);
      }
    },
  });

  ctx.y = doc.lastAutoTable.finalY + 10;
}

/* ── Disclosure + footer ─────────────────────────────────────────────── */

function drawDisclosure(ctx, data) {
  const { doc } = ctx;
  if (data.trip.confirmationDisclosed) return;
  const msg = 'Your airline confirmation number is released once your trip is fully paid and marked completed. '
    + 'Until then, this statement is your official record with Defera.';
  font(doc, 6.75, 'italic', COL.muted);
  const lines = doc.splitTextToSize(msg, CONTENT_W);
  ctx.ensure(lines.length * 8.5 + 6);
  doc.text(lines, M, ctx.y, { baseline: 'top', lineHeightFactor: 1.3 });
  ctx.y += lines.length * 8.5 + 6;
}

function drawFooter(ctx, data) {
  const { doc } = ctx;
  const brand = data.document.brand;
  const reference = data.trip.reference || data.document.number;
  const terms = `This statement is generated by Defera for your records. Amounts reflect the trip's stored payment `
    + `schedule and history at the time of download. For questions or reconciliation, reach us using the contact `
    + `information on the right — please reference your booking reference ${reference}.`;

  font(doc, 6.5, 'normal', COL.muted);
  const termsLines = doc.splitTextToSize(terms, CONTENT_W * 0.62);
  const footerH = 10 + 9 + termsLines.length * 8;

  // Pin the footer to the bottom margin when there's room; when content
  // runs long, let it sit right below the content (eating into the bottom
  // margin down to a 24pt floor) rather than spawning a mostly-empty page
  // for three lines of boilerplate.
  let footerTop = PAGE_H - BOTTOM - footerH;
  if (ctx.y + 4 > footerTop) {
    if (ctx.y + 4 + footerH <= PAGE_H - 24) {
      footerTop = ctx.y + 4;
    } else {
      doc.addPage();
      ctx.y = drawContinuationHeader(doc, ctx.reference);
    }
  }

  hRule(doc, footerTop, COL.rule, 0.6);
  upperLabel(doc, brand.name, M, footerTop + 8, 7, COL.brand);
  font(doc, 6.5, 'normal', COL.muted);
  doc.text(termsLines, M, footerTop + 18, { baseline: 'top', lineHeightFactor: 1.25 });

  const rx = PAGE_W - M;
  font(doc, 7, 'bold', COL.slate);
  doc.text(brand.website, rx, footerTop + 8, { align: 'right', baseline: 'top' });
  font(doc, 6.5, 'normal', COL.muted);
  doc.text(brand.contactPage, rx, footerTop + 17, { align: 'right', baseline: 'top' });
  doc.text(brand.supportPhone, rx, footerTop + 26, { align: 'right', baseline: 'top' });
}

function drawPageNumbers(doc) {
  const pageCount = doc.getNumberOfPages();
  if (pageCount <= 1) return;
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    font(doc, 6, 'normal', COL.faint);
    doc.text(`Page ${i} of ${pageCount}`, PAGE_W - M, PAGE_H - 20, { align: 'right', baseline: 'top' });
  }
}

/* ── Entry point ─────────────────────────────────────────────────────── */

/**
 * Build the Trip Statement as a PDF Blob.
 *
 * `data`    — output of buildTripDocumentData(). Segments must carry
 *             `marketingCarrierLogoPng` (a PNG data URI produced by
 *             DownloadTripDocumentButton's logo inlining step). jsPDF
 *             only embeds raster images — SVG is never passed here.
 * `options` — { logoDataUri?: string } PNG data URI of the Defera print
 *             logo; when absent a text wordmark is drawn instead.
 */
export async function buildTripStatementPdfBlob(data, options = {}) {
  const [{ default: JsPdf }, { default: autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);

  const doc = new JsPdf({ orientation: 'portrait', unit: 'pt', format: 'letter', compress: true });
  doc.setProperties({
    title: `${data.document.title} ${data.document.number}`,
    subject: 'Trip Statement',
    author: data.document.brand.name,
    creator: data.document.brand.name,
  });

  const ctx = makeCtx(doc, data.trip.reference || data.document.number);
  ctx.y = drawHeader(doc, data, options.logoDataUri || null);

  drawBanner(ctx, data);
  drawMetaStrip(ctx, data);
  drawPanels(ctx, data);
  drawItinerary(ctx, data);
  drawPassengers(ctx, data);

  drawSectionTitle(ctx, 'Payment plan & history');
  drawPaymentStrip(ctx, data);
  drawPaymentTable(ctx, data, autoTable);
  drawDisclosure(ctx, data);

  drawFooter(ctx, data);
  drawPageNumbers(doc);

  return doc.output('blob');
}
